import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CHAT_STRINGS_PT, ChatStrings } from '../../chat.strings';
import { ChatConnectionIssue } from '../../services/chat.service';

/** What the banner can show. `offline` = chatDisconnected$ without a known issue. */
export type ChatBannerIssue = 'offline' | ChatConnectionIssue;

/**
 * "Sem ligação. As mensagens seguem quando voltar." and the other handshake
 * refusals of contract section 1. `no_chat_identity` is not a banner: the
 * container shows the empty state for it. Styled like the theme's soft
 * warning alert; it slides in when it appears.
 */
@Component({
  selector: 'chat-connection-banner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
<div *ngIf="issue && issue !== 'no_chat_identity'" class="chat-banner" role="status" aria-live="polite">
  <span class="chat-banner__icon"><chat-icon name="wifi" [size]="16"></chat-icon></span>
  <span class="chat-banner__text">{{ text }}</span>
</div>`,
  styles: [`
:host { display: block; flex-shrink: 0; }
.chat-banner { display: flex; align-items: center; gap: 10px; padding: 8px 16px; background: rgba(var(--chat-warning-rgb), .15); color: var(--chat-warning-ink); border-bottom: 1px solid rgba(var(--chat-warning-rgb), .4); font-size: 12.5px; line-height: 1.4; animation: chat-banner-in .25s ease-out; }
.chat-banner__icon { width: 28px; height: 28px; flex-shrink: 0; border-radius: 50%; background: rgba(var(--chat-warning-rgb), .25); display: inline-flex; align-items: center; justify-content: center; }
.chat-banner__text { flex-grow: 1; }
@keyframes chat-banner-in { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .chat-banner { animation: none; } }
`]
})
export class ChatConnectionBannerComponent {
  @Input() issue: ChatBannerIssue | null = null;
  @Input() strings: ChatStrings = CHAT_STRINGS_PT;

  get text(): string {
    switch (this.issue) {
      case 'token_expired':
      case 'session_not_found':
        return this.strings.sessionExpired;
      case 'invalid_credentials':
        return this.strings.invalidCredentials;
      case 'temporarily_unavailable':
        return this.strings.temporarilyUnavailable;
      default:
        return this.strings.offline;
    }
  }
}
